import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Sparkles, ShieldCheck, Star, MapPin } from "lucide-react";
import { ALEXA_PHOTO } from "../config";
import { useLang } from "../LanguageContext";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const lines = [t.hero.title1, t.hero.title2];

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden bg-[#050505] spotlight">
      {/* Ambient gold glows on the luxury dark base (no stock background) */}
      <div className="absolute top-24 -left-24 w-80 h-80 rounded-full bg-[#d4af37]/15 blob z-0" />
      <div className="absolute bottom-0 right-1/3 w-96 h-96 rounded-full bg-[#8a6d1f]/12 blob z-0" style={{ animationDelay: "5s" }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-28 md:pt-32 pb-16 min-h-screen grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* LEFT — text */}
        <motion.div style={{ y: yText, opacity }} className="order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-xs tracking-wide text-[#e9d08a] mb-7"
          >
            <Sparkles size={14} className="text-[#d4af37]" />
            {t.hero.badge}
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display text-white leading-[0.98] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[5.2rem]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.4 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {i === 1 ? <span className="text-gold-gradient italic">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-7 max-w-xl text-base md:text-lg text-[#b8b8b8] leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("booking")}
              className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors duration-300"
            >
              {t.hero.primary}
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("services")}
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 glass text-white hover:bg-white/10 transition-colors duration-300"
            >
              {t.hero.secondary}
            </button>
          </motion.div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[#a3a3a3]"
          >
            <span className="inline-flex items-center gap-2">
              <span className="flex text-[#d4af37]">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="#d4af37" strokeWidth={0} />)}
              </span>
              {t.hero.trust}
            </span>
            <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-[#d4af37]" /> {t.hero.based}</span>
          </motion.div>
        </motion.div>

        {/* RIGHT — real photo with cinematic camera motion */}
        <motion.div style={{ y: yPhoto }} className="order-1 lg:order-2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="frame-breathe relative mx-auto max-w-md lg:max-w-none"
          >
            <div className="absolute -inset-3 rounded-[2rem] border border-[#d4af37]/25 pointer-events-none" />
            <div className="relative rounded-[1.8rem] overflow-hidden gold-glow">
              {/* Cinematic Ken Burns camera move on the real, unaltered photo */}
              <img
                src={ALEXA_PHOTO}
                alt="Alexa Guzman, professional financial consultant and founder of Alexa Financial Multiservices, in her office"
                fetchPriority="high"
                className="kenburns w-full h-[440px] sm:h-[540px] lg:h-[620px] object-cover object-top"
              />
              {/* Cinematic scrim for text legibility & mood */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/75 via-transparent to-[#050505]/10" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/25 to-transparent" />
              {/* Soft gold light sweep */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="light-sweep absolute top-0 -left-1/2 h-full w-1/3 bg-gradient-to-r from-transparent via-[#f3e5ab]/25 to-transparent" />
              </div>
              {/* Name caption */}
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl px-5 py-3.5 flex items-center justify-between">
                <div>
                  <div className="font-display text-lg text-white leading-none">Alexa Guzman</div>
                  <div className="text-xs text-[#d4af37] mt-1">{t.hero.captionRole}</div>
                </div>
                <ShieldCheck size={20} className="text-[#d4af37]" />
              </div>
            </div>

            {/* Floating accent chips */}
            <div
              className="hidden sm:flex absolute -left-5 top-16 glass rounded-2xl px-4 py-3 items-center gap-2.5"
              style={{ animation: "chip-float-a 6s ease-in-out infinite" }}
            >
              <span className="w-8 h-8 rounded-full bg-[#d4af37]/15 flex items-center justify-center text-[#d4af37] text-sm font-semibold">LLC</span>
              <div className="text-xs text-[#d4d4d4] leading-tight">2,500+<br /><span className="text-[#888]">launched</span></div>
            </div>
            <div
              className="hidden sm:flex absolute -right-4 bottom-28 glass rounded-2xl px-4 py-3 items-center gap-2.5"
              style={{ animation: "chip-float-b 7s ease-in-out infinite" }}
            >
              <span className="w-8 h-8 rounded-full bg-[#d4af37]/15 flex items-center justify-center text-[#d4af37]"><Star size={15} fill="#d4af37" strokeWidth={0} /></span>
              <div className="text-xs text-[#d4d4d4] leading-tight">98%<br /><span className="text-[#888]">satisfaction</span></div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#888]">{t.hero.scroll}</span>
        <div className="w-px h-9 bg-gradient-to-b from-[#d4af37] to-transparent" />
      </motion.div>
    </section>
  );
};
